/**
 * TransactionDetailDialog - Phase A (Feb 2026)
 *
 * View + edit completed bank-feed transactions with the same parity the
 * main table inline edits offer:
 *   - GL category change (non-bank side)
 *   - VAT toggle + rate
 *   - WHT toggle + rate
 *   - Description edit
 *   - "Save Changes" → POST /api/bank-feeds/save-changes/{tx_id}
 *
 * For pending/review_needed rows we keep the original read-only view (the
 * approval / inline edit flow on the table is the right place for those).
 *
 * Split-GL transactions: when the linked source document has multiple line
 * items (split), each line is shown read-only with a "Open document to edit
 * split" CTA - splits are managed on the document side, never duplicated here.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  ArrowUpRight, ArrowDownRight, Calendar, FileText, Building2, Clock,
  RefreshCw, Save, Pencil, Loader2, AlertTriangle, Layers, ExternalLink, FolderKanban,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { getCurrencySymbol, formatCurrency } from '../../utils/currency';
import { GlAccountInlineSelect } from './GlAccountInlineSelect';
import { ProjectSelect } from '../projects/ProjectSelect';
import { useNavigate } from 'react-router-dom';
import { showPeriodLockToast } from '../../utils/periodLockToast';

const COMPLETED_STATUSES = ['completed', 'matched', 'processed', 'created'];

export const TransactionDetailDialog = ({
  open,
  onOpenChange,
  transaction,
  formatAmount,
  bankCurrency,
  baseCurrency,
  // Phase A - editing
  availableGlAccounts = [],
  onAfterSave,                 // () => Promise<void>: parent refetches
  onNavigateToDocument,        // (doc_type, doc_id) => void
}) => {
  const isCompleted = !!transaction && COMPLETED_STATUSES.includes(transaction.status);
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [splitLines, setSplitLines] = useState(null);   // null=not loaded, []=none, [...]=split
  const [loadingSplit, setLoadingSplit] = useState(false);

  // Local edit buffer - initialized from `transaction` on open.
  const isInflow = transaction?.direction === 'inflow' || (transaction?.amount || 0) > 0;
  const [glDebit, setGlDebit]               = useState('');
  const [glCredit, setGlCredit]             = useState('');
  const [description, setDescription]       = useState('');
  const [vatApplied, setVatApplied]         = useState(false);
  const [vatRate, setVatRate]               = useState(0);
  const [whtApplied, setWhtApplied]         = useState(false);
  const [whtRate, setWhtRate]               = useState(0);
  const [projectId, setProjectId]           = useState(null);  // iter 1003

  // Reset state whenever the dialog opens for a new transaction.
  useEffect(() => {
    if (!open || !transaction) return;
    setEditMode(false);
    setSaving(false);
    setGlDebit(transaction.ai_gl_debit || '');
    setGlCredit(transaction.ai_gl_credit || '');
    setDescription(transaction.description || '');
    setVatApplied(!!transaction.apply_input_vat || !!transaction.apply_output_vat);
    setVatRate(transaction.input_vat_rate || transaction.vat_rate || 0);
    setWhtApplied(!!transaction.apply_wht || !!transaction.customer_is_wht_agent);
    setWhtRate(transaction.wht_rate || 0);
    setProjectId(transaction.project_id || null);
    setSplitLines(null);
    setPostedJournals(null);
  }, [open, transaction]);

  // Posted ledger entries for this row (every journal, every GL line).
  const [postedJournals, setPostedJournals] = useState(null);
  useEffect(() => {
    if (!open || !transaction?.id || !isCompleted) return;
    axios.get(`${API}/bank-feeds/in-books/${transaction.id}`)
      .then((res) => setPostedJournals(res.data?.journals || []))
      .catch(() => setPostedJournals([]));
  }, [open, transaction, isCompleted]);

  // Fetch linked-document line items so we can detect & show split entries.
  useEffect(() => {
    if (!open || !transaction || !isCompleted) return;
    if (!transaction.source_document_id || !transaction.source_document_type) {
      setSplitLines([]);
      return;
    }
    const docType = transaction.source_document_type;
    if (!['invoice', 'bill', 'other_payment', 'other_receipt'].includes(docType)) {
      setSplitLines([]);
      return;
    }
    setLoadingSplit(true);
    const pluralUrl = {
      invoice: 'invoices', bill: 'bills',
      other_payment: 'other-payments', other_receipt: 'other-receipts',
    }[docType];
    axios.get(`${API}/${pluralUrl}/${transaction.source_document_id}`)
      .then(res => {
        const lines = res.data?.line_items || res.data?.items || res.data?.lines || [];
        setSplitLines(lines.length > 1 ? lines : []);
      })
      .catch(() => setSplitLines([]))
      .finally(() => setLoadingSplit(false));
  }, [open, transaction, isCompleted]);

  const dirtyFields = useMemo(() => {
    if (!transaction) return {};
    const fields = {};
    if (isInflow) {
      if (glCredit !== (transaction.ai_gl_credit || '')) fields.gl_credit = glCredit;
    } else if (glDebit !== (transaction.ai_gl_debit || '')) fields.gl_debit = glDebit;
    if (description !== (transaction.description || '')) fields.description = description;
    const initialVat = !!transaction.apply_input_vat || !!transaction.apply_output_vat;
    const initialVatRate = transaction.input_vat_rate || transaction.vat_rate || 0;
    if (vatApplied !== initialVat) fields.vat_applied = vatApplied;
    if (vatApplied && Number(vatRate) !== Number(initialVatRate)) fields.vat_rate = Number(vatRate);
    const initialWht = !!transaction.apply_wht || !!transaction.customer_is_wht_agent;
    const initialWhtRate = transaction.wht_rate || 0;
    if (whtApplied !== initialWht) fields.wht_applied = whtApplied;
    if (whtApplied && Number(whtRate) !== Number(initialWhtRate)) fields.wht_rate = Number(whtRate);
    if ((projectId || null) !== (transaction.project_id || null)) {
      if (projectId) fields.project_id = projectId;
      else fields.clear_project = true;
    }
    return fields;
  }, [transaction, isInflow, glCredit, glDebit, description, vatApplied, vatRate, whtApplied, whtRate, projectId]);

  const hasChanges = Object.keys(dirtyFields).length > 0;
  const hasOnlyDescriptionChange = hasChanges
    && Object.keys(dirtyFields).length === 1
    && Object.prototype.hasOwnProperty.call(dirtyFields, 'description');

  const handleSave = async () => {
    if (!transaction || saving || !hasChanges) return;
    setSaving(true);
    try {
      // Description-only edits don't need a journal re-post - use the
      // lightweight description-update endpoint to avoid spurious activity-log
      // noise about reversed journals when nothing financial changed.
      if (hasOnlyDescriptionChange) {
        await axios.patch(`${API}/bank-feeds/transactions/${transaction.id}/description`, {
          description: dirtyFields.description,
        });
        toast.success('Description updated');
      } else {
        // Mixed/financial edits go through the atomic save-changes flow
        // which reverses the journal and re-posts with the new state. The
        // description is sent alongside so it lands in both transaction
        // and the linked document in one round trip.
        const payload = {
          vat_applied: vatApplied,
          vat_rate: vatApplied ? Number(vatRate) : 0,
          wht_applied: whtApplied,
          wht_rate: whtApplied ? Number(whtRate) : 0,
          reason: 'Edit from transaction detail view',
          ...dirtyFields,
        };
        await axios.post(`${API}/bank-feeds/save-changes/${transaction.id}`, payload);
        toast.success('Changes saved. Original entry reversed and re-posted.');
      }
      if (onAfterSave) await onAfterSave();
      setEditMode(false);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (showPeriodLockToast(err, navigate)) {
        // period-lock toast handled
      } else if (err?.response?.status === 423) {
        toast.error(msg || 'Period or reconciliation is locked: unlock first.');
      } else {
        toast.error(msg || 'Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!transaction) return null;

  const DirectionIcon = transaction.direction === 'inflow' ? ArrowDownRight : ArrowUpRight;
  const directionColor = transaction.direction === 'inflow' ? 'text-green-600' : 'text-red-600';
  const directionBg = transaction.direction === 'inflow' ? 'bg-green-50' : 'bg-red-50';
  const isForeignCurrency = bankCurrency && baseCurrency && bankCurrency !== baseCurrency;
  const fxRate = transaction.fx_rate || transaction.exchange_rate;
  const baseAmount = transaction.base_currency_amount || (fxRate ? (transaction.amount * fxRate) : null);

  const editable = editMode && isCompleted;
  const hasSplit = Array.isArray(splitLines) && splitLines.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="transaction-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Transaction Details
            {!isCompleted && (
              <Badge variant="outline" className="ml-auto" data-testid="tx-detail-pending-badge">
                Not yet completed: edits via approval flow
              </Badge>
            )}
          </DialogTitle>
          {editable && (
            <DialogDescription>
              Editing this transaction will reverse the original journal entry and
              re-post a new one. The linked document and its schedule will be updated
              in sync. Bank amount is never changed.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          {/* Amount & direction (always read-only - bank amount is sacred) */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${directionBg}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${transaction.direction === 'inflow' ? 'bg-green-100' : 'bg-red-100'}`}>
                <DirectionIcon className={`w-5 h-5 ${directionColor}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {transaction.direction === 'inflow' ? 'Money In' : 'Money Out'}
                </p>
                <p className={`text-xl font-bold ${directionColor}`}>
                  {transaction.direction === 'inflow' ? '+' : '-'}
                  {getCurrencySymbol(bankCurrency)}{formatCurrency(transaction.amount)}
                </p>
                {bankCurrency && (
                  <span className="text-xs text-muted-foreground font-medium">{bankCurrency}</span>
                )}
              </div>
            </div>
            <Badge variant="outline" className="capitalize">{transaction.status || 'Pending'}</Badge>
          </div>

          {/* FX info */}
          {isForeignCurrency && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <RefreshCw className="w-4 h-4 text-blue-600" />
                <p className="text-xs font-medium text-blue-800">Foreign Currency Conversion</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <p className="text-xs text-blue-600">Original</p>
                  <p className="font-medium">{getCurrencySymbol(bankCurrency)}{formatCurrency(transaction.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600">FX Rate</p>
                  <p className="font-medium">{fxRate ? fxRate.toFixed(4) : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600">Base ({baseCurrency})</p>
                  <p className="font-medium">{getCurrencySymbol(baseCurrency)}{baseAmount ? formatCurrency(baseAmount) : 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Date - always read-only (bank-controlled) */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Date</p>
              <p className="text-sm font-medium">{transaction.date}</p>
            </div>
          </div>

          {/* Description - editable or read-only */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <Label htmlFor="tx-detail-description" className="text-xs text-muted-foreground flex items-center gap-2">
              <FileText className="w-4 h-4" /> Description
            </Label>
            {editable ? (
              <Textarea
                id="tx-detail-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="tx-detail-description-input"
              />
            ) : (
              <p className="text-sm font-medium mt-1">{transaction.description || 'No description'}</p>
            )}
          </div>

          {/* Project - for not-yet-completed rows, allocate now; saved immediately
              and inherited by the journal when the row is approved/posted. */}
          {!isCompleted && (
            <div className="p-3 bg-muted/50 rounded-lg" data-testid="tx-detail-project-row">
              <Label className="text-xs text-muted-foreground flex items-center gap-2">
                <FolderKanban className="w-4 h-4" /> Project (optional)
              </Label>
              <div className="mt-1">
                <ProjectSelect
                  compact
                  value={projectId}
                  onChange={async (pid) => {
                    setProjectId(pid);
                    try {
                      await axios.post(`${API}/bank-feeds/save-changes/${transaction.id}`,
                        pid ? { project_id: pid } : { clear_project: true });
                      toast.success(pid ? 'Project assigned: carries into the posted entry' : 'Project cleared');
                      if (onAfterSave) await onAfterSave();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Could not update project');
                    }
                  }}
                  createdFrom="bank_feed"
                  autoMatched={!!transaction.project_auto_matched && projectId === transaction.project_id}
                />
              </div>
            </div>
          )}

          {/* Counterparty - read-only (matched/inferred from bank narration) */}
          {transaction.counterparty && (
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Building2 className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Counterparty</p>
                <p className="text-sm font-medium">{transaction.counterparty}</p>
              </div>
            </div>
          )}

          {/* Classification - read-only (changed via the reclassify dialog) */}
          {transaction.classification && (
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">AI Classification</p>
                <p className="text-sm font-medium capitalize">
                  {transaction.classification?.replace(/_/g, ' ')}
                </p>
              </div>
            </div>
          )}

          {/* Decision trail — who/what chose this posting and why (Sep 2026 audit) */}
          {(transaction.ai_confidence != null || transaction.classification_method || transaction.processing_reasoning || transaction.ai_reasoning) && (
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg" data-testid="tx-detail-decision-trail">
              <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">How this was decided</p>
                <p className="text-sm font-medium" data-testid="tx-detail-decision-method">
                  {(() => {
                    const m = String(transaction.classification_method || '');
                    if (m === 'user') return 'You confirmed this category';
                    if (m.startsWith('deterministic')) return 'Smart Match · bank-charge pattern (no AI)';
                    if (m.startsWith('rule') || m === 'learned') return 'Smart Match · learned from your earlier approvals';
                    return transaction.ai_posting_action === 'auto_post' ? 'AI Assist · posted automatically' : 'AI Assist · suggestion';
                  })()}
                  {transaction.ai_confidence != null && (
                    <span className="ml-2 text-xs text-muted-foreground">{Math.round(Number(transaction.ai_confidence) * 100)}% confidence</span>
                  )}
                </p>
                {(transaction.ai_gl_reasoning || transaction.processing_reasoning || transaction.ai_reasoning) && (
                  <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line break-words" data-testid="tx-detail-decision-reason">
                    {[transaction.ai_gl_reasoning, transaction.processing_reasoning || transaction.ai_reasoning]
                      .filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' — ')}
                  </p>
                )}
                {transaction.reclassification_reason && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="tx-detail-decision-reclass">Changed: {transaction.reclassification_reason}</p>
                )}
              </div>
            </div>
          )}

          {/* Posted ledger entries — the actual GL lines, every journal (recognition + settlement, splits, tax, FX) */}
          {isCompleted && Array.isArray(postedJournals) && postedJournals.length > 0 ? (
            <div className="p-3 bg-muted/50 rounded-lg space-y-3" data-testid="tx-detail-posted-journals">
              <p className="text-xs text-muted-foreground">Posted to ledger</p>
              {postedJournals.map((j) => (
                <div key={j.id} className="rounded border bg-white" data-testid={`tx-detail-journal-${j.id}`}>
                  <div className="flex items-center justify-between px-2 py-1 text-[11px] text-muted-foreground border-b">
                    <span className="truncate">{String(j.source_type || 'journal').replace(/_/g, ' ')} · {j.date}</span>
                    {j.reference && <span className="font-mono truncate ml-2">{j.reference}</span>}
                  </div>
                  {(j.lines || []).map((ln, idx) => {
                    const isDr = (ln.debit || 0) > 0;
                    const func = isDr ? ln.debit : ln.credit;
                    const nat = isDr ? ln.native_debit : ln.native_credit;
                    const showNative = ln.native_currency && ln.native_currency !== baseCurrency && nat != null;
                    return (
                      <div key={idx} className="flex items-start justify-between gap-2 px-2 py-1.5 text-xs border-b last:border-b-0">
                        <div className="min-w-0">
                          <span className="font-medium">{ln.account_code}</span>
                          {ln.account_name && <span className="text-muted-foreground"> · {ln.account_name}</span>}
                          {ln.description && <span className="text-muted-foreground block truncate">{ln.description}</span>}
                        </div>
                        <div className="text-right whitespace-nowrap tabular-nums">
                          <span className={isDr ? 'text-emerald-700' : 'text-rose-700'}>{isDr ? 'Dr' : 'Cr'} {formatCurrency(func, baseCurrency)}</span>
                          {showNative && (
                            <span className="block text-[10px] text-muted-foreground">{formatCurrency(nat, ln.native_currency)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (transaction.ai_gl_debit || transaction.ai_gl_credit) && !hasSplit && (
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <p className="text-xs text-muted-foreground">GL Accounts</p>
              {editable ? (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {isInflow ? 'Debit (Bank, fixed)' : 'Debit (Category, editable)'}
                    </Label>
                    {isInflow ? (
                      <p className="text-sm font-medium">{glDebit || '-'}</p>
                    ) : (
                      <GlAccountInlineSelect
                        accounts={availableGlAccounts}
                        value={glDebit}
                        onChange={(acc) => setGlDebit(acc?.code || '')}
                        testId="tx-detail-gl-debit"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {isInflow ? 'Credit (Category, editable)' : 'Credit (Bank, fixed)'}
                    </Label>
                    {isInflow ? (
                      <GlAccountInlineSelect
                        accounts={availableGlAccounts}
                        value={glCredit}
                        onChange={(acc) => setGlCredit(acc?.code || '')}
                        testId="tx-detail-gl-credit"
                      />
                    ) : (
                      <p className="text-sm font-medium">{glCredit || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Project (optional)</Label>
                    <div className="mt-1">
                      <ProjectSelect value={projectId} onChange={setProjectId} createdFrom="bank_feed" compact
                        autoMatched={!!transaction.project_auto_matched && projectId === transaction.project_id} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Debit: </span>
                    <span className="font-medium">{transaction.ai_gl_debit}</span>
                    {transaction.ai_gl_debit_name && (
                      <span className="text-xs text-muted-foreground block">{transaction.ai_gl_debit_name}</span>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Credit: </span>
                    <span className="font-medium">{transaction.ai_gl_credit}</span>
                    {transaction.ai_gl_credit_name && (
                      <span className="text-xs text-muted-foreground block">{transaction.ai_gl_credit_name}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Split-GL display: read-only line items + CTA to edit on the doc */}
          {hasSplit && (
            <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2" data-testid="tx-detail-split-section">
              <div className="flex items-center gap-2 text-violet-800">
                <Layers className="w-4 h-4" />
                <span className="text-xs font-medium">Split transaction: {splitLines.length} line items</span>
              </div>
              <div className="text-xs text-violet-700">
                Split allocations live on the linked document so they remain the single source
                of truth. Edit splits there to keep the schedule in sync.
              </div>
              <div className="bg-white rounded border border-violet-200 max-h-40 overflow-y-auto">
                {splitLines.map((line, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-1.5 text-xs border-b last:border-b-0 border-violet-100">
                    <div className="flex-1 truncate">
                      <span className="font-medium">{line.gl_code || line.account_code}</span>
                      {line.gl_name || line.account_name ? (
                        <span className="text-muted-foreground"> · {line.gl_name || line.account_name}</span>
                      ) : null}
                      {line.description && (
                        <span className="text-muted-foreground block truncate">{line.description}</span>
                      )}
                    </div>
                    <span className="font-medium tabular-nums">{formatAmount?.(line.amount || line.total) ?? line.amount}</span>
                  </div>
                ))}
              </div>
              {onNavigateToDocument && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => onNavigateToDocument(transaction.source_document_type, transaction.source_document_id)}
                  className="w-full"
                  data-testid="tx-detail-split-open-doc"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open document to edit split
                </Button>
              )}
            </div>
          )}

          {/* VAT toggle */}
          {(editable || transaction.apply_input_vat || transaction.apply_output_vat) && !hasSplit && (
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Apply VAT</Label>
                {editable ? (
                  <Switch
                    checked={vatApplied}
                    onCheckedChange={setVatApplied}
                    data-testid="tx-detail-vat-toggle"
                  />
                ) : (
                  <Badge variant={vatApplied ? 'default' : 'secondary'}>{vatApplied ? 'On' : 'Off'}</Badge>
                )}
              </div>
              {vatApplied && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Rate (%)</Label>
                  {editable ? (
                    <Input
                      type="number" step="0.01" value={vatRate}
                      onChange={(e) => setVatRate(e.target.value)}
                      className="w-24 h-8"
                      data-testid="tx-detail-vat-rate"
                    />
                  ) : (
                    <span className="text-sm font-medium">{vatRate}%</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* WHT toggle */}
          {(editable || transaction.apply_wht || transaction.customer_is_wht_agent) && !hasSplit && (
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Apply WHT</Label>
                {editable ? (
                  <Switch
                    checked={whtApplied}
                    onCheckedChange={setWhtApplied}
                    data-testid="tx-detail-wht-toggle"
                  />
                ) : (
                  <Badge variant={whtApplied ? 'default' : 'secondary'}>{whtApplied ? 'On' : 'Off'}</Badge>
                )}
              </div>
              {whtApplied && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Rate (%)</Label>
                  {editable ? (
                    <Input
                      type="number" step="0.01" value={whtRate}
                      onChange={(e) => setWhtRate(e.target.value)}
                      className="w-24 h-8"
                      data-testid="tx-detail-wht-rate"
                    />
                  ) : (
                    <span className="text-sm font-medium">{whtRate}%</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reference (always read-only) */}
          {transaction.reference && (
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Reference</p>
                <p className="text-sm font-medium font-mono">{transaction.reference}</p>
              </div>
            </div>
          )}

          {/* Period-lock hint (info only - server enforces) */}
          {editable && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                If the original entry is in a locked period, you must unlock the
                period first or the save will be rejected (HTTP 423).
              </span>
            </div>
          )}

          {loadingSplit && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking for split allocations…
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {editable ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setEditMode(false)}
                disabled={saving}
                data-testid="tx-detail-cancel-edit"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                data-testid="tx-detail-save-changes"
              >
                {saving ? (<><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving…</>)
                  : (<><Save className="w-4 h-4 mr-1" /> Save Changes</>)}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-detail-dialog">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDetailDialog;
